<template>
  <div class="relative flex flex-col items-center">
    <div
      ref="surfaceContainer"
      class="relative rounded bg-gray-100 dark:bg-charcoal-800"
      @dragover.prevent="onDragOver"
      @dragleave="onDragLeave"
      @drop="onDrop"
      @click.stop
    >
      <!-- design in full scale -->
      <div :style="scaledCoverStyle">
        <div
          class="relative bg-white origin-top-left overflow-hidden"
          :class="{'shadow-md': !isExport}"
          :style="surfaceStyle"
        >
          <div class="absolute-cover">
            <surface-editor-element
              v-for="element in renderElements"
              :key="element.id"
              :surface="surface"
              :element="element"
              :content-override="getContentOverride(element)"
              :render-scale="effectiveScale"
            />
          </div>
        </div>
      </div>

      <!-- non-scaled ui elements (EDITOR ONLY) -->
      <div v-if="!isExport" class="absolute-cover" :style="scaledCoverStyle">
        <div
          class="absolute pointer-events-none"
          :class="{
            'z-50': !selectedElement?.background && selectedElement?.id === element.id
          }"
          v-for="element in renderElements"
          :key="element.id"
          :style="surfaceEditorStore.getScaledElementPositionStyle({element, includeRotation: false})"
        >
          <surface-editor-element-context-bar
            v-if="selectedElement?.id === element.id && !element.background && !isDragging && !isRotating && !editedElement"
            :element="element"
            class="absolute left-1/2 -translate-x-1/2 -translate-y-full z-[9999] pointer-events-auto"
          />

          <div
            class="absolute-cover outline outline-2 outline-transparent pointer-events-auto"
            :class="[
              {
                'hover:outline-heroblue': selectedElement?.id !== element.id
              }
            ]"
            :style="{
              transform: `rotate(${element.rotation || 0}deg)`
            }"
            @pointerdown.stop="onElementWrapperPointerDown($event, element)"
            @dblclick="event => edit({element, event})"
          >
            <surface-editor-element-ui-container
              :class="{
                'opacity-0 pointer-events-none': selectedElement?.id !== element.id,
                'opacity-100': selectedElement?.id === element.id,
                'hidden': editedElement
              }"
              :element="element"
              :resizable="selectedElement?.id === element.id"
              :on-drag-start="startDrag"
              @pointerdown="onPointerDown($event, element)"
            />
          </div>
        </div>

        <surface-editor-element-edit
          v-if="editedElement"
          :surface="surface"
          :element="editedElement"
          :initiating-event="editInitiationEvent"
          class="pointer-events-auto"
        />

        <surface-editor-position-guides v-if="positionGuidesEnabled" :surface="surface" />
      </div>
    </div>
    <div v-if="isExport" class="absolute-cover" />

    <!-- Drag tooltip -->
    <teleport to="body">
      <div
        v-if="showTooltip"
        class="fixed z-[99999] pointer-events-none rounded bg-black/80 px-2.5 py-1.5 text-xs text-white font-semibold border border-white/50 shadow-lg"
        :style="tooltipStyle"
      >
        {{tooltipText}}
      </div>
    </teleport>
  </div>
</template>

<script setup>
  import {storeToRefs} from 'pinia';
  import {SURFACE_EDITOR_ELEMENT_TYPE} from '~/constants/surface-editor.js';

  const props = defineProps({
    surface: { type: Object, required: true },
    mode: { type: String, default: 'editor' }, // 'editor' | 'export'
    exportScale: { type: Number, default: null } // if set, overrides store scale
  });

  const surfaceEditorStore = useSurfaceEditorStore();
  const {scale, selectedElement, editedElement, contextMenuState, positionGuidesEnabled, isRotating, isDragging} = storeToRefs(surfaceEditorStore);
  const {
    buildTextElement,
    getShapePresets,
    buildShapeElement,
    buildPhotoElement,
    buildPhotoContentForElement
  } = useSurfaceEditorElementFactory();
  const {dragPayload, clearDragPayload} = useSurfaceEditorDragState();
  const {startDrag, showTooltip, tooltipText, tooltipStyle, stopDrag} = useSurfaceEditorElementDrag();

  const isExport = computed(() => props.mode === 'export');

  const effectiveScale = computed(() => {
    return props.exportScale ?? scale.value ?? 1;
  });

  const backgroundElement = computed(() => props.surface.background);
  const renderElements = computed(() => [backgroundElement.value, ...props.surface.elements]);

  const containerDimensions = computed(() => ({
    width: props.surface.width * effectiveScale.value,
    height: props.surface.height * effectiveScale.value
  }));

  const scaledCoverStyle = computed(() => ({
    width: `${containerDimensions.value.width}px`,
    height: `${containerDimensions.value.height}px`
  }));

  const surfaceStyle = computed(() => ({
    width: `${props.surface.width}px`,
    height: `${props.surface.height}px`,
    transform: `scale(${effectiveScale.value})`
  }));

  const surfaceContainer = ref(null);
  const previewTargetElementId = ref(null);
  const previewContent = ref(null);

  function parseDragPayload(event) {
    const raw = event.dataTransfer?.getData('application/json');

    if (!raw) {
      return dragPayload.value;
    }

    try {
      return JSON.parse(raw);
    } catch (error) {
      return dragPayload.value;
    }
  }

  function getSurfacePoint(event) {
    const rect = surfaceContainer.value?.getBoundingClientRect();

    if (!rect) {
      return null;
    }

    return {
      x: (event.clientX - rect.left) / effectiveScale.value,
      y: (event.clientY - rect.top) / effectiveScale.value
    };
  }

  function clearPreview() {
    previewTargetElementId.value = null;
    previewContent.value = null;
  }

  function isPointInsideElement(point, element) {
    return point.x >= element.x
      && point.x <= element.x + element.width
      && point.y >= element.y
      && point.y <= element.y + element.height;
  }

  function isLargeBackgroundElement(element) {
    return element.width * element.height >= props.surface.width * props.surface.height * 0.99;
  }

  function isWithinEdgeZone(point, element) {
    const marginX = element.width * 0.15;
    const marginY = element.height * 0.15;

    return point.x <= element.x + marginX
      || point.x >= element.x + element.width - marginX
      || point.y <= element.y + marginY
      || point.y >= element.y + element.height - marginY;
  }

  function getTopmostPhotoAtPoint(point) {
    for (let index = props.surface.elements.length - 1; index >= 0; index -= 1) {
      const element = props.surface.elements[index];

      if (element.type !== SURFACE_EDITOR_ELEMENT_TYPE.image) {
        continue;
      }

      if (!isPointInsideElement(point, element)) {
        continue;
      }

      if (isLargeBackgroundElement(element) && !isWithinEdgeZone(point, element)) {
        return null;
      }

      return element;
    }

    return null;
  }

  function getContentOverride(element) {
    if (element.id !== previewTargetElementId.value) {
      return null;
    }

    return previewContent.value;
  }

  function onDragOver(event) {
    const payload = parseDragPayload(event);

    if (!payload || payload.type !== 'photo') {
      clearPreview();
      return;
    }

    const point = getSurfacePoint(event);

    if (!point) {
      clearPreview();
      return;
    }

    const target = getTopmostPhotoAtPoint(point);

    if (!target) {
      clearPreview();
      return;
    }

    if (!payload.images?.original) {
      clearPreview();
      return;
    }

    const content = buildPhotoContentForElement({
      element: target,
      file: {
        images: payload.images,
        width: payload.width,
        height: payload.height
      }
    });

    if (!content) {
      clearPreview();
      return;
    }

    previewTargetElementId.value = target.id;
    previewContent.value = content;
  }

  function onDragLeave(event) {
    if (surfaceContainer.value?.contains(event.relatedTarget)) {
      return;
    }

    clearPreview();
  }

  function onDrop(event) {
    const payload = parseDragPayload(event);

    if (!payload) {
      clearPreview();
      clearDragPayload();
      return;
    }

    const point = getSurfacePoint(event);

    switch (payload.type) {
      case 'template': {
        if (payload.body) {
          surfaceEditorStore.applyTemplate({
            template: payload.body,
            surface: props.surface
          });
        }
        break;
      }
      case 'text': {
        const preset = payload.preset;

        if (!preset) {
          break;
        }

        const element = buildTextElement({preset, surface: props.surface, position: point});

        if (element) {
          surfaceEditorStore.addElement({element, surface: props.surface});
        }
        break;
      }
      case 'shape': {
        const preset = getShapePresets(props.surface).find(shape => shape.id === payload.id);
        const element = buildShapeElement({preset, position: point});

        if (element) {
          surfaceEditorStore.addElement({element, surface: props.surface});
        }
        break;
      }
      case 'photo': {
        const file = {
          images: payload.images,
          width: payload.width,
          height: payload.height,
          id: payload.id
        };
        const target = props.surface.elements.find(element => element.id === previewTargetElementId.value);

        if (target && payload.images?.original) {
          const content = buildPhotoContentForElement({element: target, file});

          if (content) {
            surfaceEditorStore.updateElement({
              element: target,
              updates: {
                content
              }
            });
          }
        } else {
          const element = buildPhotoElement({file, surface: props.surface});

          if (element) {
            surfaceEditorStore.addElement({element, surface: props.surface});
          }
        }
        break;
      }
      default:
        break;
    }

    clearPreview();
    clearDragPayload();
  }

  function select({element}) {
    // no-op in export mode (should be gated by template anyway, but extra safety)
    if (isExport.value) return;

    surfaceEditorStore.closeContextMenu();
    surfaceEditorStore.selectElement({element});

    if (element?.id !== editedElement?.id) {
      surfaceEditorStore.editElement({element: null});
    }
  }

  function onElementWrapperPointerDown(event, element) {
    if (isExport.value) return;

    // Select the element if not already selected
    if (selectedElement.value?.id !== element.id) {
      select({element});
    }

    // Start dragging the entire element
    startDrag(event, element, SURFACE_EDITOR_DRAG_HANDLE.all);
  }

  let editInitiationEvent = null;

  async function edit({element, event}) {
    if (isExport.value) return;

    if (editedElement.value && element?.id === backgroundElement.value.id) {
      return;
    }

    editInitiationEvent = event;
    select({element});
    await nextTick();
    surfaceEditorStore.editElement({element});
  }

  function onPointerDown(event, element) {
    if (event.button === 2) { // right mouse button
      onContextMenu(event, element);
    }
  }

  function onContextMenu(event, element) {
    if (isExport.value) return;

    if (contextMenuState.value.isOpen) {
      event.stopPropagation();
    } else {
      event.preventDefault();
      event.stopPropagation();
      surfaceEditorStore.openContextMenu({
        element,
        x: event.clientX,
        y: event.clientY
      });
    }

  }

  watch(editedElement, (newVal) => {
    if (!newVal) {
      editInitiationEvent = null;
    }
  })

  onBeforeUnmount(stopDrag);
</script>
